/**
 * StaffShell — Layout dell'ambiente STAFF (CRM v7).
 *
 * Header pulito con:
 *   [ Sarabi Staff ]                        [ Home ]  [ operatore ]  [ Esci ]
 *
 * Nessuna sidebar, nessun elemento admin. Se non autenticato, redirect
 * alla tastiera PIN. Se autenticato, mostra il layout con Outlet.
 */
import React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Home, LogOut, Cog } from "lucide-react";
import { useGlobalWhatsAppSound } from "@/lib/useGlobalWhatsAppSound";
import { hasPermission } from "@/lib/permissions";
import { ConnectivityBanner } from "@/components/ConnectivityBanner";

// M2.3.1 · Auth-lost listener: se il refresh token è realmente scaduto
// (401 anche dal refresh), il client viene rimandato al login.
// Cross-device recovery: preserva route corrente prima del redirect così
// che il pin-login post autenticazione possa ripristinare il contesto.
if (typeof window !== "undefined" && !window.__sbAuthLostBound) {
  window.__sbAuthLostBound = true;
  window.addEventListener("sb-auth-lost", () => {
    if (window.location.pathname.startsWith("/staff")
        && !window.location.pathname.includes("/login")) {
      try {
        sessionStorage.setItem(
          "sb.postLogin.redirect",
          window.location.pathname + window.location.search);
      } catch { /* ignore */ }
      window.location.href = "/staff/login";
    }
  });
}

const ROLE_LABEL = {
  super_admin: "Super Admin", admin: "Admin", manager: "Manager",
  bar: "Bar", sala: "Sala", cucina: "Cucina", spiaggia: "Spiaggia",
  bartender: "Bartender", runner: "Runner", analyst: "Analista",
};

export default function StaffShell() {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  // M2.2.6 · Sound engine GLOBALE: un solo poller/dedup montato dallo shell
  // persistente, così un nuovo inbound produce beep da QUALUNQUE pagina /staff/*
  // (Home compresa). Vedi `/app/frontend/src/lib/useGlobalWhatsAppSound.js`.
  const soundEnabled = !!user && hasPermission(user, "staff.messaging");
  useGlobalWhatsAppSound({ enabled: soundEnabled });

  if (loading) return <div className="p-8 text-ink-500">Caricamento…</div>;
  if (!user) return <Navigate to="/staff/login" replace state={{ from: loc.pathname }} />;

  const doLogout = async () => {
    // Best-effort: call the dedicated PIN logout for audit; then use the
    // AuthProvider's logout() which ALSO clears the bearer token stored
    // in localStorage — without this, /auth/me would keep re-authenticating
    // the user via the fallback header on the next page load.
    try { await api.post("/auth/pin-logout"); } catch { /* ignore */ }
    // Reset seenIds del sound engine (evita beep spuri al prossimo login).
    try { window.__sarabiResetGlobalSound && window.__sarabiResetGlobalSound(); }
    catch { /* ignore */ }
    await logout();
    toast.success("Sessione chiusa");
    nav("/staff/login", { replace: true });
  };

  const isHome = loc.pathname === "/staff" || loc.pathname === "/staff/";
  const isAdminUser = ["super_admin", "admin", "manager"].includes(user?.role)
                        && (user?.role === "super_admin"
                            || (user?.effective_permissions || []).includes("admin.access"));

  return (
    <div className="min-h-screen bg-sand-50 flex flex-col" data-testid="staff-shell">
      <ConnectivityBanner />
      <header className="sticky top-0 z-30 bg-white border-b border-sand-200 px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
        <Link to="/staff" className="font-serif text-lg whitespace-nowrap" data-testid="staff-brand">
          <span className="text-bronze-700">Sarabi</span> Staff
        </Link>
        <div className="flex-1" />
        {!isHome && (
          <NavLink to="/staff" end
                     data-testid="staff-nav-home"
                     className="text-sm rounded-full px-3 py-1.5 border border-sand-300
                                 hover:bg-sand-100 inline-flex items-center gap-1">
            <Home className="w-4 h-4" /> <span className="hidden sm:inline">Home</span>
          </NavLink>
        )}
        <div className="text-right text-xs min-w-0" data-testid="staff-operator">
          <div className="font-medium truncate max-w-[90px] sm:max-w-[160px]">{user.name || user.email}</div>
          <div className="text-ink-500">{ROLE_LABEL[user.role] || user.role}</div>
        </div>
        {isAdminUser && (
          <a href="/admin" title="Backoffice"
              data-testid="staff-open-admin"
              className="text-xs rounded-full px-3 py-1.5 border border-sand-300 hover:bg-sand-100
                          inline-flex items-center gap-1">
            <Cog className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Admin</span>
          </a>
        )}
        <button onClick={doLogout}
                 data-testid="staff-logout"
                 className="text-sm rounded-full px-3 py-1.5 border border-rose-300 text-rose-700
                             hover:bg-rose-50 inline-flex items-center gap-1">
          <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Esci</span>
        </button>
      </header>

      <main className="flex-1 p-4 md:p-6">
        <Outlet />
      </main>

      <footer className="text-center text-[10px] text-ink-400 py-3">
        Sarabi Staff · v7 · {new Date().toLocaleDateString("it-IT")}
      </footer>
    </div>
  );
}
